/**
 * Native tool performance for a single drop (A/B, batch, shortlink, sms blocks).
 * Feeds the "Tool (SMSWiser)" stats tab — separate source from Voluum/Binom.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useQueries, useQuery } from '@tanstack/react-query';

import api from '@/services/api';
import { createConcurrencyGate } from '@/lib/concurrency-gate';

import type { NormalizedDropStats } from './tool-stats.types';

// The "All drops" view fans out one request per drop. Firing them all at once
// trips the backend's 20 req/s throttler AND SMSWiser's stricter per-drop stats
// limit (~2/s) → most 429 and the table fills only partially. Gate every fetch
// through a small concurrency limiter so they queue client-side and all
// complete. Concurrency alone isn't enough (fast calls → high rate), so we also
// pace the START rate to ~1.8/s — just under SMSWiser's ~2/s per-request ceiling
// (the backend additionally backs off + retries 429s). True speed needs a
// batch/campaign stats endpoint from SMSWiser; drop-by-drop can't beat their rate
// limit. ~50 drops therefore takes ~30s, but ALL of them load.
const dropStatsGate = createConcurrencyGate(2, 600);

function fetchDropStats(uniqueId: string): Promise<NormalizedDropStats> {
  return dropStatsGate.run(async () => {
    const res = await api.get<{ data: NormalizedDropStats }>(
      `/smswiser/tool-stats/drops/${encodeURIComponent(uniqueId)}`,
    );
    return res.data.data;
  });
}

export function useToolDropStats(uniqueId: string, enabled = true) {
  return useQuery({
    queryKey: ['smswiser', 'tool-stats', 'drop', uniqueId],
    enabled: enabled && !!uniqueId,
    queryFn: () => fetchDropStats(uniqueId),
    staleTime: 60_000,
  });
}

/**
 * Fetch native stats for several drops at once (the aggregated "All drops" view).
 * Shares the per-drop query cache, so switching back to a single drop is instant.
 */
export function useToolDropStatsMulti(uniqueIds: string[], enabled = true) {
  return useQueries({
    queries: uniqueIds.map((uniqueId) => ({
      queryKey: ['smswiser', 'tool-stats', 'drop', uniqueId],
      enabled: enabled && !!uniqueId,
      queryFn: () => fetchDropStats(uniqueId),
      staleTime: 60_000,
    })),
  });
}
